/**
 * surgicalRegulatingModule.js - 2026-10-18 (Tab 3 review, Dr. Tatar: surgical regulating)
 *
 * Routes the surgical cases of one brigade-day that its Role 2 Basic cannot take:
 *   offered   = cases of the day + the backlog carried in;
 *   limit     = the capacity at which the teams would go into CRISIS tempo (LOAD_RATIO_CRISIS_FROM x the normal capacity);
 *   excess    = offered above the limit;
 *   routed    = the excess offered, in order, to a neighbour Role 2 Basic (only when the planner says one exists, or the force has
 *               several brigades), the Role 2 Enhanced and the Role 3 (each only while accessible), up to the capacity each can
 *               give this brigade;
 *   queued    = what nobody takes stays in the Role 2 Basic queue (backlog).
 * Each routed case pays the extra transit of its destination; the weighted average over the cases handled is returned in hours
 * and acts like extra wait for surgery. Constants and their status: constants/surgicalRegulating.js.
 * Scope of this first version: the destinations are assumed to have no load of their own (their capacity is the share of the
 * brigade); bypass of the Role 2 Basic by T1 casualties and the simultaneous routing across several brigades are not built.
 */
import { distributeCivilianCases } from './civilianLoad.js';
import { LOAD_RATIO_CRISIS_FROM, averageShiftPerformance } from '../../constants/surgicalTempo.js';
import {
  REGULATING_DEFAULTS,
  EXTRA_TRANSIT_MIN_PER_KM,
  FACILITY_SURGICAL_TEAMS
} from '../../constants/surgicalRegulating.js';

const num = (v, fallback) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const pct = (v, fallback) => {
  const x = num(v, fallback);
  if (x < 0 || x > 100) throw new Error(`surgicalRegulatingModule: a share must be from 0 to 100 percent (got: ${v})`);
  return x / 100;
};

/**
 * The destinations of the excess and what each can take of one brigade's cases in a day.
 * @param {Object} p
 * @param {Object} p.medicalConfig          medical_configuration (surgical_regulating, r2e_accessible, r3_accessible, mtf_distances, r2e_distance_km, surgical_config, facilities)
 * @param {number} p.brigadeTeams           surgical team equivalents of this brigade's Role 2 Basic
 * @param {number} p.brigadeCount           brigades of the force
 * @param {number} p.normalCasesPerTeam     cases a team does in a day at the normal tempo
 */
export function buildSurgicalDestinations({ medicalConfig, brigadeTeams, brigadeCount, normalCasesPerTeam, civilianCases = 0 }) {
  const cfg = { ...REGULATING_DEFAULTS, ...(medicalConfig?.surgical_regulating || {}) };
  const perf = averageShiftPerformance(8);   // destinations work at the normal tempo (first version)
  const dist = medicalConfig?.mtf_distances || {};
  const r2bKm = num(dist.role2_distance_km, 0);
  const chain = medicalConfig?.chain_distances || {};   // legs: Role 1 -> 2B, 2B -> 2E, 2E -> Role 3 (chainDistances.js)
  const r2bToR2eKm = chain.r2b_to_r2e?.km;
  // Role 2B to Role 3: through the Role 2 Enhanced when it exists; with no Role 2E the leg typed for Role 3 is read from the Role 2B
  const r2bToR3Km = chain.r2e_to_r3?.km == null ? null
    : (medicalConfig?.r2e_accessible === false ? chain.r2e_to_r3.km : (r2bToR2eKm == null ? null : r2bToR2eKm + chain.r2e_to_r3.km));
  const out = [];
  let r2eFull = null;
  let r3Full = null;

  // Neighbour Role 2 Basic: explicit planner input in a one-brigade scenario; present automatically in a force of several brigades.
  const neighbourExists = cfg.neighbour_r2b_enabled === true || (brigadeCount || 1) >= 2;
  if (neighbourExists) {
    out.push({
      id: 'NEIGHBOUR_R2B',
      capacity: Math.max(0, brigadeTeams) * normalCasesPerTeam * perf * pct(cfg.neighbour_r2b_share_percent, 25),
      extra_min: EXTRA_TRANSIT_MIN_PER_KM * Math.max(0, num(cfg.neighbour_r2b_distance_km, 20))
    });
  }

  // Role 2 Enhanced: division asset; the Surgical Company of Tab 3 is its attached capacity (2 team equivalents each).
  if (medicalConfig?.r2e_accessible !== false) {
    const attachedCompanies = num(Number(medicalConfig?.surgical_config?.teams?.SURG_CO?.count), 0);
    const teams = FACILITY_SURGICAL_TEAMS.ROLE2_ENHANCED + attachedCompanies * 2;
    const r2eExtraKm = r2bToR2eKm != null ? r2bToR2eKm : Math.max(0, num(medicalConfig?.r2e_distance_km, num(dist.role2_enhanced_km, r2bKm)) - r2bKm);
    const fullCap = teams * normalCasesPerTeam * perf;
    r2eFull = fullCap;
    out.push({
      id: 'ROLE2_ENHANCED',
      capacity: fullCap * pct(cfg.r2e_share_percent, 33),
      extra_min: EXTRA_TRANSIT_MIN_PER_KM * r2eExtraKm
    });
  }

  // Role 3: corps asset.
  if (medicalConfig?.r3_accessible !== false) {
    const r3ExtraKm = r2bToR3Km != null ? r2bToR3Km : Math.max(0, num(dist.role3_distance_km, r2bKm) - r2bKm);
    const fullCap = FACILITY_SURGICAL_TEAMS.ROLE3 * normalCasesPerTeam * perf;
    r3Full = fullCap;
    out.push({
      id: 'ROLE3',
      capacity: fullCap * pct(cfg.r3_share_percent, 33),
      extra_min: EXTRA_TRANSIT_MIN_PER_KM * r3ExtraKm
    });
  }
  // R2-08: the civilian cases the military takes (rules of engagement and host system) go to the Role 2 Enhanced and Role 3 first: their capacity falls by what the
  // civilians use, in the same share the brigade has of the facility; what they cannot take is returned for the Role 2 Basic.
  let civilianToR2B = Math.max(0, civilianCases);
  if (civilianCases > 0) {
    const order = [];
    if (r2eFull !== null) order.push({ id: 'ROLE2_ENHANCED', full_capacity: r2eFull });
    if (r3Full !== null) order.push({ id: 'ROLE3', full_capacity: r3Full });
    const split = distributeCivilianCases(civilianCases, order);
    out.forEach((d) => {
      const share = d.id === 'ROLE2_ENHANCED' ? pct(cfg.r2e_share_percent, 33) : d.id === 'ROLE3' ? pct(cfg.r3_share_percent, 33) : null;
      if (share !== null) d.capacity = Math.max(0, d.capacity - (split.taken[d.id] || 0) * share);
      d.civilian_cases_taken = split.taken[d.id] || 0;
    });
    civilianToR2B = split.to_r2b;
  }
  return { enabled: cfg.enabled !== false, destinations: out, civilian_to_r2b: civilianToR2B };
}

/**
 * @param {Object} p
 * @param {number} p.demandCases  cases of the day
 * @param {number} p.backlog      backlog carried in
 * @param {number} p.teams        surgical team equivalents of the Role 2 Basic
 * @param {number} p.normalCasesPerTeam
 * @param {Array}  p.destinations from buildSurgicalDestinations
 */
export function regulateSurgicalCases({ demandCases, backlog, teams, normalCasesPerTeam, destinations }) {
  const offered = Math.max(0, demandCases) + Math.max(0, backlog);
  const limit = LOAD_RATIO_CRISIS_FROM * Math.max(0, teams) * normalCasesPerTeam;
  const excess = Math.max(0, offered - limit);
  let left = excess;
  const routed = {};
  let routedTotal = 0;
  let extraMinCases = 0;
  destinations.forEach((d) => {
    const take = Math.min(left, Math.max(0, d.capacity));
    routed[d.id] = take;
    left -= take;
    routedTotal += take;
    extraMinCases += take * d.extra_min;
  });
  const accepted = Math.min(offered, limit);
  const handled = accepted + routedTotal;
  // routed cases come off the new cases first, then off the backlog
  const newCasesAfter = Math.max(0, Math.max(0, demandCases) - routedTotal);
  const backlogAfter = Math.max(0, Math.max(0, backlog) - Math.max(0, routedTotal - Math.max(0, demandCases)));
  return {
    offered,
    limit,
    accepted_r2b: accepted,
    excess,
    routed,
    routed_total: routedTotal,
    queued_beyond_limit: Math.max(0, left),
    new_cases_for_r2b: newCasesAfter,
    backlog_for_r2b: backlogAfter,
    // average extra transit per case handled, in hours: acts like extra wait for surgery
    extra_delay_hours: handled > 0 ? extraMinCases / handled / 60 : 0
  };
}
